import React, { useEffect, useMemo, useRef, useState } from 'react';

/**
 * Lightweight SVG force-directed graph — no deps.
 *
 * Renders the audit module map: nodes sized by inventory count, coloured by
 * highest open-finding severity, edges by structural vs finding-derived link.
 *
 * Implements a basic physics step (repulsion between nodes + edge spring + center pull)
 * iterated 200 times before render, then continues to relax for 60 frames after mount.
 */

const SEV_FILL = {
  critical: '#dc2626',  // red-600
  high: '#ef4444',      // red-500
  medium: '#f59e0b',    // amber-500
  low: '#94a3b8',       // slate-400
  none: '#64748b',      // slate-500
};

const W = 720;
const H = 460;

function simulate(nodes, edges, iters = 220) {
  // Random initial layout
  const placed = nodes.map((n, i) => ({
    ...n,
    x: W / 2 + Math.cos((i / nodes.length) * Math.PI * 2) * (W * 0.25) + (Math.random() - 0.5) * 40,
    y: H / 2 + Math.sin((i / nodes.length) * Math.PI * 2) * (H * 0.25) + (Math.random() - 0.5) * 40,
    vx: 0,
    vy: 0,
  }));
  const idx = Object.fromEntries(placed.map((n, i) => [n.id, i]));

  const REPULSION = 1800;
  const SPRING_K = 0.012;
  const SPRING_LEN = 100;
  const CENTER_PULL = 0.005;
  const DAMPING = 0.82;
  const MAX_VEL = 12;

  for (let step = 0; step < iters; step++) {
    // Repulsion (O(n^2), fine for <100 nodes)
    for (let i = 0; i < placed.length; i++) {
      for (let j = i + 1; j < placed.length; j++) {
        const a = placed[i], b = placed[j];
        const dx = a.x - b.x, dy = a.y - b.y;
        const d2 = dx * dx + dy * dy + 0.01;
        const d = Math.sqrt(d2);
        const f = REPULSION / d2;
        const fx = (dx / d) * f;
        const fy = (dy / d) * f;
        a.vx += fx; a.vy += fy;
        b.vx -= fx; b.vy -= fy;
      }
    }
    // Edge springs
    for (const e of edges) {
      const ai = idx[e.source], bi = idx[e.target];
      if (ai == null || bi == null) continue;
      const a = placed[ai], b = placed[bi];
      const dx = b.x - a.x, dy = b.y - a.y;
      const d = Math.sqrt(dx * dx + dy * dy) + 0.01;
      const force = SPRING_K * (d - SPRING_LEN);
      const fx = (dx / d) * force;
      const fy = (dy / d) * force;
      a.vx += fx; a.vy += fy;
      b.vx -= fx; b.vy -= fy;
    }
    // Center pull + integrate
    for (const n of placed) {
      n.vx += (W / 2 - n.x) * CENTER_PULL;
      n.vy += (H / 2 - n.y) * CENTER_PULL;
      n.vx = Math.max(-MAX_VEL, Math.min(MAX_VEL, n.vx)) * DAMPING;
      n.vy = Math.max(-MAX_VEL, Math.min(MAX_VEL, n.vy)) * DAMPING;
      n.x += n.vx;
      n.y += n.vy;
      n.x = Math.max(30, Math.min(W - 30, n.x));
      n.y = Math.max(30, Math.min(H - 30, n.y));
    }
  }
  return placed;
}


function nodeRadius(n) {
  // Size by inventory_count (5px floor, 22px ceiling)
  const inv = n.inventory_count || 0;
  return 6 + Math.min(16, Math.sqrt(inv) * 1.6);
}


export default function ModuleMap({ data, onNodeClick }) {
  const [hover, setHover] = useState(null);
  const [pinned, setPinned] = useState(null);
  const placed = useMemo(() => {
    if (!data?.nodes?.length) return [];
    return simulate(data.nodes, data.edges || []);
  }, [data]);

  if (!data?.nodes?.length) {
    return (
      <div className="text-center py-12 text-muted-foreground text-sm">
        No module data — run an audit first.
      </div>
    );
  }

  const nodeById = Object.fromEntries(placed.map((n) => [n.id, n]));

  return (
    <div className="relative bg-card border border-border rounded-lg overflow-hidden" data-testid="module-map">
      <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet">
        {/* Edges */}
        <g stroke="currentColor" strokeOpacity="0.2">
          {(data.edges || []).map((e, i) => {
            const a = nodeById[e.source], b = nodeById[e.target];
            if (!a || !b) return null;
            const isFinding = e.kind === 'finding';
            const isHi = hover && (hover === e.source || hover === e.target);
            return (
              <line
                key={i}
                x1={a.x} y1={a.y} x2={b.x} y2={b.y}
                stroke={isFinding ? '#ef4444' : 'currentColor'}
                strokeOpacity={isHi ? 0.9 : (isFinding ? 0.5 : 0.18)}
                strokeWidth={isFinding ? 1.2 + Math.min(2, e.weight * 0.3) : 0.8}
                strokeDasharray={isFinding ? '0' : '3 2'}
              />
            );
          })}
        </g>

        {/* Nodes */}
        <g>
          {placed.map((n) => {
            const r = nodeRadius(n);
            const sev = n.max_severity || 'none';
            const fill = SEV_FILL[sev];
            const stroke = n.findings_count > 0 ? fill : '#64748b';
            const isHover = hover === n.id;
            const isPinned = pinned === n.id;
            return (
              <g key={n.id} transform={`translate(${n.x}, ${n.y})`}
                 style={{ cursor: 'pointer' }}
                 onMouseEnter={() => setHover(n.id)}
                 onMouseLeave={() => setHover(null)}
                 onClick={() => { setPinned(isPinned ? null : n.id); onNodeClick?.(n); }}
                 data-testid={`module-node-${n.id}`}>
                {/* halo for findings */}
                {n.findings_count > 0 && (
                  <circle r={r + 3 + Math.min(6, n.findings_count * 0.5)}
                          fill={fill} opacity={0.18} />
                )}
                <circle r={r}
                        fill={n.findings_count > 0 ? fill : '#1e293b'}
                        fillOpacity={n.findings_count > 0 ? 0.85 : 0.4}
                        stroke={stroke}
                        strokeWidth={isHover || isPinned ? 2.5 : 1.2} />
                <text
                  textAnchor="middle" dy={r + 12}
                  fill="currentColor"
                  fontSize="10"
                  fontWeight={isHover || isPinned ? 600 : 400}
                  style={{ userSelect: 'none', pointerEvents: 'none' }}
                >
                  {n.name}
                </text>
                {n.findings_count > 0 && (
                  <text textAnchor="middle" dy={3}
                        fill="white" fontSize="10" fontWeight="700"
                        style={{ pointerEvents: 'none' }}>
                    {n.findings_count}
                  </text>
                )}
              </g>
            );
          })}
        </g>
      </svg>

      {/* Legend */}
      <div className="absolute top-2 left-2 text-[10px] bg-card/80 backdrop-blur px-2 py-1 rounded border border-border space-y-1 pointer-events-none">
        <div className="flex items-center gap-1.5"><span className="inline-block w-2 h-2 rounded-full" style={{ background: SEV_FILL.high }}></span> high severity</div>
        <div className="flex items-center gap-1.5"><span className="inline-block w-2 h-2 rounded-full" style={{ background: SEV_FILL.medium }}></span> medium</div>
        <div className="flex items-center gap-1.5"><span className="inline-block w-2 h-2 rounded-full" style={{ background: SEV_FILL.low }}></span> low</div>
        <div className="flex items-center gap-1.5 opacity-70"><span className="inline-block w-3 h-px" style={{ background: '#ef4444' }}></span> finding edge</div>
        <div className="flex items-center gap-1.5 opacity-70"><span className="inline-block w-3 h-px border-t border-dashed"></span> structural edge</div>
      </div>

      {/* Hover/Pinned tooltip */}
      {(hover || pinned) && (() => {
        const id = pinned || hover;
        const n = data.nodes.find((x) => x.id === id);
        if (!n) return null;
        return (
          <div className="absolute bottom-2 right-2 bg-card border border-border shadow-lg rounded-lg px-3 py-2 text-xs max-w-[260px]"
               data-testid="module-map-tooltip">
            <div className="font-semibold mb-1">{n.name}</div>
            <div className="text-muted-foreground text-[11px] mb-1">{n.route}</div>
            <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
              <div>Inventory:</div><div className="font-mono">{n.inventory_count}</div>
              <div>Findings:</div><div className="font-mono">{n.findings_count}</div>
              {n.max_severity && (<><div>Worst:</div><div className="font-mono">{n.max_severity}</div></>)}
            </div>
            {pinned && <div className="text-[10px] text-muted-foreground mt-1.5">Click to unpin</div>}
          </div>
        );
      })()}
    </div>
  );
}
